import React from "react";
import { Box, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";
import type { IOfflineStall } from "../../type/offlineExhibitorType";

export interface ExhibitorStallPassCardProps {
	stall: IOfflineStall;
	companyName?: string;
	width?: number | string;
	isPrinting?: boolean;
}

export const getStallQrCodeUrl = (stallNo: string, size = 450) => {
	return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
		stallNo || ""
	)}`;
};

export const downloadStallPassPNG = async (
	stall: IOfflineStall,
	element?: HTMLElement | null
) => {
	if (!stall) return;
	try {
		const target =
			element ||
			document.getElementById(`stall-pass-card-${stall.id || stall.stall_no}`) ||
			(document.querySelector(".stall-pass-card-container") as HTMLElement);

		if (target) {
			const dataUrl = await toPng(target, {
				quality: 1,
				pixelRatio: 3,
				backgroundColor: "#ffffff",
				cacheBust: true,
				includeQueryParams: true,
			});
			saveAs(dataUrl, `BTTF-Stall-Pass-${stall.stall_no}.png`);
		}
	} catch (error) {
		console.error("Failed to download stall pass:", error);
	}
};

const ExhibitorStallPassCard = React.forwardRef<
	HTMLDivElement,
	ExhibitorStallPassCardProps
>(({ stall, companyName, width = "100%", isPrinting = false }, ref) => {
	const qrUrl = getStallQrCodeUrl(stall?.stall_no || "", 450);
	const displayName =
		stall?.exhibitor_name || companyName || "EXHIBITOR COMPANY";

	const nameLength = displayName.length;
	// Dynamically adjust font size, line-height, and label spacing based on text length
	const companyFontSize =
		nameLength > 42
			? "2.7cqw"
			: nameLength > 28
				? "3.2cqw"
				: nameLength > 18
					? "3.7cqw"
					: "4.2cqw";

	const companyLineHeight = nameLength > 25 ? 1.08 : 1.18;
	const companyLabelSize = nameLength > 30 ? "2.2cqw" : "2.6cqw";
	const companyLabelMargin = nameLength > 25 ? "0.3cqw" : "0.5cqw";

	// Stall No dynamic sizing for long stall names (e.g. "Pakistan Pavilion - 05")
	const stallNoStr = String(stall?.stall_no || "N/A");
	const stallNoLength = stallNoStr.length;
	const stallFontSize =
		stallNoLength > 24
			? "2.6cqw"
			: stallNoLength > 18
				? "3.0cqw"
				: stallNoLength > 12
					? "3.6cqw"
					: stallNoLength > 6
						? "4.3cqw"
						: "5.0cqw";

	const stallLineHeight = stallNoLength > 10 ? 1.05 : 1.12;
	const stallLabelSize = stallNoLength > 14 ? "2.2cqw" : "2.5cqw";
	const stallLabelMargin = stallNoLength > 14 ? "0.2cqw" : "0.4cqw";

	// Hall Name dynamic sizing (e.g. "HARMONY HALL")
	const hallNameStr = String(stall?.hall_name || "N/A");
	const hallNameLength = hallNameStr.length;
	const hallFontSize =
		hallNameLength > 22
			? "2.3cqw"
			: hallNameLength > 15
				? "2.5cqw"
				: hallNameLength > 9
					? "2.9cqw"
					: hallNameLength > 5
						? "3.3cqw"
						: "3.9cqw";

	const hallLineHeight = hallNameLength > 9 ? 1.05 : 1.12;
	const hallLabelSize = hallNameLength > 9 ? "2.1cqw" : "2.5cqw";
	const hallLabelMargin = hallNameLength > 9 ? "0.2cqw" : "0.4cqw";

	return (
		<Box
			ref={ref}
			id={`stall-pass-card-${stall?.id || stall?.stall_no}`}
			className="stall-pass-card-container exhibitor-stall-pass-card"
			sx={{
				position: "relative",
				width: width || 330,
				maxWidth: 340,
				aspectRatio: "971 / 1619",
				borderRadius: isPrinting ? "0px" : "18px",
				overflow: "hidden",
				boxShadow: isPrinting ? "none" : "0 10px 32px rgba(0, 0, 0, 0.16)",
				bgcolor: "#ffffff",
				fontFamily: "'Outfit', 'Poppins', 'Roboto', sans-serif",
				userSelect: "none",
				mx: "auto",
				containerType: "inline-size",
				mt: 2
			}}
		>
			{/* Background Template Graphic */}
			<Box
				component="img"
				src={"/exhibitor-stall-qr-bg.png"
				}
				alt="BTTF-2026 Exhibitor Stall Pass"
				sx={{
					position: "absolute",
					inset: 0,
					width: "100%",
					height: "100%",
					objectFit: "fill",
					zIndex: 1,
					display: "block",
					pointerEvents: "none",
				}}
			/>

			{/* Dynamic Content Overlay Layer */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					width: "100%",
					height: "100%",
					zIndex: 2,
				}}
			>
				{/* 1. Middle QR Code Frame (Centered inside the template's corner bracket box) */}
				<Box
					sx={{
						position: "absolute",
						top: "44.5%",
						left: "50%",
						transform: "translateX(-50%)",
						width: "36.8%",
						aspectRatio: "1 / 1",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						p: "0.8cqw",
						bgcolor: "#ffffff",
						borderRadius: "8px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
					}}
				>
					{stall?.stall_no ? (
						<Box
							key={stall.stall_no}
							component="img"
							src={qrUrl}
							alt={`QR for Stall ${stall.stall_no}`}
							crossOrigin="anonymous"
							sx={{
								width: "100%",
								height: "100%",
								objectFit: "contain",
								display: "block",
							}}
						/>
					) : null}
				</Box>

				{/* 2. White Card Area at Bottom (Company Name, Stall No, Hall Name) */}
				<Box
					sx={{
						position: "absolute",
						top: "71.3%",
						left: "10.6%",
						width: "78.8%",
						height: "18.8%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						alignItems: "stretch",
						gap: "1.2cqw",
						textAlign: "center",
						p: "2.2cqw",
						boxSizing: "border-box",
					}}
				>
					{/* Company Name Box */}
					<Box
						sx={{
							width: "100%",
							flex: 1,
							bgcolor: "rgba(3, 105, 161, 0.08)",
							borderRadius: "10px",
							py: "1.2cqw",
							px: "2.0cqw",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 0,
						}}
					>
						<Typography
							sx={{
								fontSize: companyLabelSize,
								fontWeight: 800,
								color: "#0369a1",
								textTransform: "uppercase",
								letterSpacing: "0.8px",
								fontFamily: "'Poppins', sans-serif",
								lineHeight: 1,
								mb: companyLabelMargin,
								whiteSpace: "nowrap",
							}}
						>
							COMPANY NAME
						</Typography>
						<Typography
							sx={{
								fontSize: companyFontSize,
								fontWeight: 900,
								color: "#0f172a",
								fontFamily: "'Outfit', 'Poppins', sans-serif",
								lineHeight: companyLineHeight,
								textAlign: "center",
								textTransform: "uppercase",
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
								textOverflow: "ellipsis",
								wordBreak: "break-word",
								maxWidth: "100%",
							}}
							title={displayName}
						>
							{displayName}
						</Typography>
					</Box>

					{/* Stall No & Hall Name Badges */}
					<Box
						sx={{
							display: "flex",
							alignItems: "stretch",
							justifyContent: "center",
							gap: "1.5cqw",
							width: "100%",
							flex: 1,
							minHeight: 0,
						}}
					>
						{/* Stall No Box */}
						<Box
							sx={{
								flex: stallNoLength > 12 && hallNameLength <= 8 ? 1.25 : 1,
								minWidth: 0,
								bgcolor: "rgba(226, 8, 111, 0.08)",
								borderRadius: "10px",
								py: "0.8cqw",
								px: "1.2cqw",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								minHeight: 0,
								overflow: "hidden",
							}}
						>
							<Typography
								sx={{
									fontSize: stallLabelSize,
									fontWeight: 800,
									color: "#e2086f",
									textTransform: "uppercase",
									letterSpacing: "0.6px",
									fontFamily: "'Poppins', sans-serif",
									lineHeight: 1,
									mb: stallLabelMargin,
									whiteSpace: "nowrap",
								}}
							>
								STALL NO
							</Typography>
							<Typography
								sx={{
									fontSize: stallFontSize,
									fontWeight: 900,
									color: "#be123c",
									fontFamily: "'Outfit', 'Poppins', sans-serif",
									lineHeight: stallLineHeight,
									textAlign: "center",
									textTransform: "uppercase",
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
									textOverflow: "ellipsis",
									wordBreak: "break-word",
									maxWidth: "100%",
								}}
								title={stallNoStr}
							>
								{stallNoStr}
							</Typography>
						</Box>

						{/* Hall Name Box */}
						<Box
							sx={{
								flex: hallNameLength > 9 && stallNoLength <= 8 ? 1.25 : 1,
								minWidth: 0,
								bgcolor: "rgba(79, 70, 229, 0.08)",
								borderRadius: "10px",
								py: "0.8cqw",
								px: "1.2cqw",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								minHeight: 0,
								overflow: "hidden",
							}}
						>
							<Typography
								sx={{
									fontSize: hallLabelSize,
									fontWeight: 800,
									color: "#4f46e5",
									textTransform: "uppercase",
									letterSpacing: "0.6px",
									fontFamily: "'Poppins', sans-serif",
									lineHeight: 1,
									mb: hallLabelMargin,
									whiteSpace: "nowrap",
								}}
							>
								HALL NAME
							</Typography>
							<Typography
								sx={{
									fontSize: hallFontSize,
									fontWeight: 900,
									color: "#0f172a",
									fontFamily: "'Outfit', 'Poppins', sans-serif",
									lineHeight: hallLineHeight,
									textAlign: "center",
									textTransform: "uppercase",
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
									textOverflow: "ellipsis",
									wordBreak: "break-word",
									maxWidth: "100%",
								}}
								title={hallNameStr}
							>
								{hallNameStr}
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
});

export default ExhibitorStallPassCard;
